import type { RowData, TableColumnGroup, TableColumnMeta } from './tanstack-table'

const OTHER_GROUP: TableColumnGroup = { label: 'Other', order: Number.POSITIVE_INFINITY }

/**
 * Column categories for `meta.group` by key, numbered in the order given, which is menu order:
 * `createColumnGroups([['load', 'Load & status'], ['team', 'Team & commissions']])`.
 */
export const createColumnGroups = <const TKey extends string>(
  groups: readonly (readonly [key: TKey, label: string])[],
) =>
  // SAFETY: built from `groups`, so it holds a group for every key in TKey.
  Object.fromEntries(groups.map(([key, label], order) => [key, { label, order }])) as Record<
    TKey,
    TableColumnGroup
  >

/**
 * `meta.hasValue` typed by row, for a table of `TData` rows:
 * `hasValueFor<Load>(load => load.drops.length > 2)`.
 */
export const hasValueFor =
  <TData extends RowData>(check: (row: TData) => boolean): TableColumnMeta['hasValue'] =>
  original =>
    // SAFETY: meta belongs to a column of a TData table, which hands it that table's rows.
    check(original as TData)

/**
 * Items under their categories, in category order with uncategorised items under Other, last.
 * Each category keeps its items in the order given.
 */
export const groupByCategory = <TItem>(
  items: readonly TItem[],
  getGroup: (item: TItem) => TableColumnGroup | undefined,
) => {
  const groups = new Map<string, { group: TableColumnGroup; items: TItem[] }>()

  for (const item of items) {
    const group = getGroup(item) ?? OTHER_GROUP
    const entry = groups.get(group.label) ?? { group, items: [] }
    entry.items.push(item)
    groups.set(group.label, entry)
  }

  return [...groups.values()].sort((first, second) => first.group.order - second.group.order)
}

/** Days table settings mark a column New after its `addedOn`. */
const NEW_DAYS = 14
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Whether the Known columns rule covers a column: its `addedOn` is after its table's rollout.
 * Both are ISO dates (`YYYY-MM-DD`), so they compare as text.
 */
export const isAddedAfterRollout = (
  addedOn: string | undefined,
  rollout: string | undefined,
): addedOn is string => addedOn !== undefined && rollout !== undefined && addedOn > rollout

/**
 * Whether table settings mark a column or Column field New: the Known columns rule covers it
 * (`isAddedAfterRollout`) and it is less than `NEW_DAYS` after its `addedOn`. The date reads as
 * UTC, so the period ends at the same moment for everyone.
 */
export const isMarkedNew = (addedOn: string | undefined, rollout: string | undefined) =>
  isAddedAfterRollout(addedOn, rollout) && Date.now() < Date.parse(addedOn) + NEW_DAYS * DAY_MS
